import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/site/Seo";
import { destinations } from "@/data/destinations";
import { services } from "@/data/services";
import { countryCodes } from "@/data/content";
import { http, errMsg } from "@/lib/api";

const STEPS = [
  { key: "destination", title: "Where would you like to go?", options: [...destinations.map((d) => d.name), "Not yet decided"] },
  { key: "service", title: "Which service interests you?", options: services.map((s) => s.name) },
  { key: "age", title: "Your age bracket", options: ["Under 25", "25 – 32", "33 – 39", "40 – 44", "45 – 54", "55 and above"] },
  { key: "education", title: "Highest level of education", options: ["Secondary school", "Diploma / Trade", "Bachelor's degree", "Master's degree", "Doctorate", "Professional qualification"] },
  { key: "experience", title: "Years of professional experience", options: ["Less than 1 year", "1 – 3 years", "3 – 5 years", "5 – 8 years", "8 – 15 years", "15+ years / Business owner"] },
  { key: "budget", title: "Budget or investment capacity", options: ["Up to ₹25 lakh", "₹25 lakh – ₹1 crore", "₹1 – ₹5 crore", "₹5 – ₹15 crore", "₹15 crore +", "Prefer to discuss privately"] },
  { key: "contact", title: "Where should we send your assessment?" },
];

const OptionGrid = ({ step, value, onPick }) => (
  <div className="grid gap-3 sm:grid-cols-2">
    {step.options.map((o, i) => (
      <button key={o} type="button" onClick={() => onPick(o)} data-testid={`eligibility-option-${step.key}-${i}`} aria-pressed={value === o} className={`group flex items-center justify-between border px-6 py-5 text-left font-serif text-xl transition-colors duration-500 ${value === o ? "border-gold bg-navy text-ivory" : "border-navy/15 bg-white text-navy hover:border-gold"}`}>
        {o}
        <span className={`flex h-6 w-6 items-center justify-center rounded-full border transition-colors ${value === o ? "border-gold bg-gold text-navy" : "border-navy/25"}`}>{value === o && <Check className="h-3.5 w-3.5" />}</span>
      </button>
    ))}
  </div>
);

const Confirmation = ({ answers }) => (
  <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }} className="mx-auto max-w-3xl text-center" data-testid="eligibility-confirmation">
    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: "spring", stiffness: 120 }} className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-gold text-gold"><Check className="h-9 w-9" strokeWidth={1.25} /></motion.div>
    <p className="eyebrow-light mt-10 justify-center">Assessment received</p>
    <h2 className="h-display mt-6 text-5xl text-ivory md:text-6xl">Thank you, {answers.name?.split(" ")[0]}.</h2>
    <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-ivory/80">A senior adviser will review your profile in confidence and contact you to discuss the pathways that may be open to you, subject to eligibility.</p>
    <dl className="mx-auto mt-12 grid max-w-2xl gap-px bg-gold/20 text-left sm:grid-cols-2">
      {STEPS.slice(0, 6).map((s) => (
        <div key={s.key} className="bg-navy-800 px-6 py-5"><dt className="text-[11px] uppercase tracking-[0.22em] text-gold">{s.key}</dt><dd className="mt-1 font-serif text-xl text-ivory">{answers[s.key]}</dd></div>
      ))}
    </dl>
    <div className="mt-12 flex flex-col justify-center gap-4 sm:flex-row">
      <Link to="/contact" className="btn-gold" data-testid="confirmation-book-btn"><span>Book a Private Consultation</span></Link>
      <Link to="/" className="btn-outline-light" data-testid="confirmation-home-btn">Return home</Link>
    </div>
  </motion.div>
);

export default function Eligibility() {
  const [i, setI] = useState(0);
  const [a, setA] = useState({ country_code: "+91" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const step = STEPS[i];
  const pct = ((i + (done ? 1 : 0)) / STEPS.length) * 100;

  const pick = (v) => { setA((p) => ({ ...p, [step.key]: v })); setTimeout(() => setI((x) => Math.min(x + 1, STEPS.length - 1)), 280); };
  const set = (k) => (e) => setA((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const answers = Object.fromEntries(STEPS.slice(2, 6).map((s) => [s.key, a[s.key]]));
      await http.post("/leads", { type: "eligibility", name: a.name, email: a.email, country_code: a.country_code, phone: a.phone, destination: a.destination, service: a.service, answers });
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="grain relative min-h-screen overflow-hidden bg-navy pb-24 pt-36 text-ivory md:pt-44" data-testid="eligibility-page">
      <Seo title="Eligibility Assessment" description="Take a confidential two-minute eligibility assessment and discover the visa, residence and investment pathways that may be open to you." />
      <img src="/img/toronto.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-10" />
      <div className="container-lux relative z-10">
        {done ? <Confirmation answers={a} /> : (
          <div className="mx-auto max-w-4xl">
            <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.25em]">
              <span className="text-gold">Eligibility Assessment</span>
              <span className="text-ivory/75" data-testid="eligibility-step-counter">Step {i + 1} of {STEPS.length}</span>
            </div>
            <div className="mt-4 h-px w-full bg-ivory/15" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100} data-testid="eligibility-progress">
              <motion.div className="h-px bg-gold" animate={{ width: `${Math.max(pct, 4)}%` }} transition={{ duration: 0.8, ease: [0.2, 0.7, 0.2, 1] }} />
            </div>
            <div className="mt-6 hidden gap-2 md:flex">
              {STEPS.map((s, k) => <button key={s.key} type="button" disabled={k > i} onClick={() => setI(k)} className={`text-[10px] uppercase tracking-[0.2em] ${k === i ? "text-gold" : k < i ? "text-ivory/80 hover:text-gold" : "text-ivory/40"}`}>{s.key}{k < STEPS.length - 1 && <span className="mx-2 text-ivory/30">/</span>}</button>)}
            </div>
            <motion.div key={step.key} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }} className="mt-14" data-testid={`eligibility-step-${step.key}`}>
              <h1 className="h-display text-4xl sm:text-5xl lg:text-6xl">{step.title}</h1>
              <div className="mt-12 [&_button]:text-lg">
                {step.options ? <OptionGrid step={step} value={a[step.key]} onPick={pick} /> : (
                  <form onSubmit={submit} className="grid gap-8 bg-ivory p-8 text-navy md:grid-cols-2 md:p-12" data-testid="eligibility-contact-form">
                    <label className="md:col-span-2"><span className="label-lux">Full name</span><input required minLength={2} className="input-lux" value={a.name || ""} onChange={set("name")} data-testid="eligibility-name-input" /></label>
                    <label><span className="label-lux">Email</span><input required type="email" className="input-lux" value={a.email || ""} onChange={set("email")} data-testid="eligibility-email-input" /></label>
                    <label><span className="label-lux">Phone</span>
                      <div className="flex gap-3">
                        <select className="input-lux !w-24" value={a.country_code} onChange={set("country_code")} data-testid="eligibility-country-code-select" aria-label="Country code">{countryCodes.map((c) => <option key={c}>{c}</option>)}</select>
                        <input required minLength={5} type="tel" className="input-lux" value={a.phone || ""} onChange={set("phone")} data-testid="eligibility-phone-input" />
                      </div>
                    </label>
                    <p className="text-[12px] leading-relaxed text-charcoal-soft md:col-span-2">Your information is confidential and used only to respond to your enquiry. This assessment is indicative and does not constitute a determination of eligibility.</p>
                    <button type="submit" disabled={busy} className="btn-navy md:col-span-2" data-testid="eligibility-submit-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit assessment"}</button>
                  </form>
                )}
              </div>
            </motion.div>
            <div className="mt-10 flex justify-between">
              <button type="button" onClick={() => setI(Math.max(0, i - 1))} disabled={i === 0} className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.2em] text-ivory/80 hover:text-gold disabled:opacity-30" data-testid="eligibility-back-btn"><ArrowLeft className="h-4 w-4" />Back</button>
              {step.options && a[step.key] && <button type="button" onClick={() => setI(i + 1)} className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.2em] text-gold" data-testid="eligibility-next-btn">Continue<ArrowRight className="h-4 w-4" /></button>}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
